import type {
  DetailedForestStand,
  MeasurementInventoryState,
  ResolvedState,
  SiteCharacteristics,
  StandDataSourceMetadata,
  TreeStandSummary,
  TreeStandTimeSeries,
  TimeSeriesSemanticType,
} from '@/types/forest-detailed';
import type { StandFieldDiff } from '@/types/measurements';

export interface ResolvedStandStatePayload {
  stand_id: string;
  property_id: string;
  effective_measurement_source: string;
  effective_recommendation_source: string;
  effective_stand_attributes?: Record<string, unknown> | null;
  provenance?: Record<string, unknown> | null;
  diff_summary?: StandFieldDiff[] | null;
  baseline_freshness?: ResolvedState['baseline_freshness'] | null;
  historical_operations?: ResolvedState['historical_operations'] | null;
  last_regeneration_year?: number | null;
  has_source_override?: boolean;
  has_corrections?: boolean;
  corrected_fields?: string[] | null;
  measurement_strata?: DetailedForestStand['tree_strata'] | null;
  campaign_id?: number | null;
  /** Explicit measurement data availability signal from backend.
   *  - 'available': strata present
   *  - 'no_tree_inventory': measured source selected but snapshot has no strata.
   *    With measurement_inventory_state === 'empty_clear_cut' this is a VALID,
   *    simulatable clear-cut (bare land regenerated by the simulation),
   *    not missing data.
   *  - 'unavailable': no snapshot found
   *  - null/undefined: metsakeskus source (not applicable) */
  measurement_data_status?: 'available' | 'stale' | 'no_tree_inventory' | 'unavailable' | null;
  measurement_inventory_state?: MeasurementInventoryState | null;
  simulation_job_id?: string | null;
  simulation_branch_id?: string | null;
  /** Best-NPV branch; present when simulation_job_id is set but no user
   *  branch choice has been persisted yet. */
  default_branch_id?: string | null;
}

export function classifyTimeSeriesDataType(dataType?: string | null): TimeSeriesSemanticType {
  switch (dataType) {
    case '1':
      return 'measurement';
    case '2':
      return 'calculation';
    case '3':
      return 'forecast';
    default:
      return 'unknown';
  }
}

/**
 * Defaults for optional site-characteristic fields. Applied only where the
 * source value is null/undefined (never overriding a real value), so absent
 * optional data becomes an explicit null/empty rather than staying undefined.
 */
const SITE_CHARACTERISTIC_DEFAULTS: Partial<SiteCharacteristics> = {
  cutting_restriction: null,
  cutting_restriction_ends: null,
  silviculture_restrictions: [],
  ditching_year: null,
  thinning_year: null,
  area_decrease: null,
  stand_quality: null,
};

export function normalizeSiteCharacteristics(
  site?: SiteCharacteristics | null,
): SiteCharacteristics {
  const normalized: SiteCharacteristics = { ...site } as SiteCharacteristics;
  for (const [key, fallback] of Object.entries(SITE_CHARACTERISTIC_DEFAULTS)) {
    const current = (normalized as Record<string, unknown>)[key];
    if (current === undefined || current === null) {
      (normalized as Record<string, unknown>)[key] = fallback;
    }
  }
  return normalized;
}

function normalizeStandSummary(summary?: TreeStandSummary | null): TreeStandSummary | null {
  if (!summary) {
    return null;
  }

  return {
    ...summary,
    dominant_height: summary.dominant_height ?? null,
    tree_species_summary: summary.tree_species_summary ?? null,
  };
}

export function normalizeTimeSeriesEntry(entry: TreeStandTimeSeries): TreeStandTimeSeries {
  return {
    ...entry,
    tree_strata: entry.tree_strata ?? [],
    stand_summary: normalizeStandSummary(entry.stand_summary),
    semantic_type: classifyTimeSeriesDataType(entry.data_type),
  };
}

export function normalizeDetailedForestStand(stand: DetailedForestStand): DetailedForestStand {
  return {
    ...stand,
    site_characteristics: normalizeSiteCharacteristics(stand.site_characteristics),
    time_series_data: (stand.time_series_data ?? []).map(normalizeTimeSeriesEntry),
    management_operations: stand.management_operations ?? [],
    dead_tree_strata: stand.dead_tree_strata ?? [],
    stand_info: stand.stand_info ?? null,
    data_source_metadata: stand.data_source_metadata ?? null,
  };
}

function coerceNumeric(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function sumStrataMetric(
  strata: DetailedForestStand['tree_strata'],
  selector: (stratum: DetailedForestStand['tree_strata'][number]) => number | null | undefined,
): number | null {
  if (!Array.isArray(strata) || strata.length === 0) {
    return null;
  }
  const values = strata.map((stratum) => coerceNumeric(selector(stratum)));
  let total = 0;
  for (const value of values) {
    if (value === null) {
      return null;
    }
    total += value;
  }
  return Number.isFinite(total) ? total : null;
}

function buildResolvedMetadata(resolved: ResolvedStandStatePayload): ResolvedState {
  return {
    has_source_override: resolved.has_source_override ?? false,
    has_corrections: resolved.has_corrections ?? false,
    corrected_fields: resolved.corrected_fields ?? [],
    measurement_source: resolved.effective_measurement_source,
    recommendation_source: resolved.effective_recommendation_source,
    baseline_freshness: resolved.baseline_freshness ?? null,
    provenance: resolved.provenance ?? {},
    diff_summary: resolved.diff_summary ?? [],
    historical_operations: resolved.historical_operations ?? [],
    last_regeneration_year: resolved.last_regeneration_year ?? null,
    simulation_job_id: resolved.simulation_job_id ?? null,
    simulation_branch_id: resolved.simulation_branch_id ?? null,
    default_branch_id: resolved.default_branch_id ?? null,
  };
}

const EFFECTIVE_SITE_FIELDS = new Set([
  'fertility_class',
  'site_type',
  'soil_type',
  'drainage_state',
  'development_class',
]);

const EFFECTIVE_SUMMARY_FIELDS: Record<string, string> = {
  mean_age: 'mean_age',
  basal_area_m2_ha: 'total_basal_area',
  stems_per_ha: 'total_stem_count',
  mean_diameter_cm: 'mean_diameter',
  mean_height_m: 'mean_height',
  dominant_height_m: 'dominant_height',
  total_volume: 'total_volume',
  saw_log_volume: 'total_sawlog_volume',
  pulp_wood_volume: 'total_pulpwood_volume',
};

const EFFECTIVE_TOP_LEVEL_FIELDS: Record<string, string> = {
  mean_age: 'mean_age',
  basal_area_m2_ha: 'total_basal_area',
  stems_per_ha: 'total_stem_count',
  mean_diameter_cm: 'mean_diameter',
  mean_height_m: 'mean_height',
  dominant_height_m: 'dominant_height',
  total_volume: 'total_volume',
  saw_log_volume: 'saw_log_volume',
  pulp_wood_volume: 'pulp_wood_volume',
};

/**
 * Overlay the resolution layer's effective stand attributes onto `nextStand`
 * and its site characteristics in place. Only defined values are applied;
 * numeric fields coerce and reject non-finite input (NO-FALLBACK).
 */
function applyEffectiveAttributes(
  nextStand: DetailedForestStand,
  nextSiteCharacteristics: SiteCharacteristics,
  effectiveAttrs: Record<string, unknown>,
): void {
  const currentEntry =
    nextStand.time_series_data?.find((entry) => entry.data_type === '2') ??
    nextStand.time_series_data?.find(
      (entry) => entry.date === nextStand.data_date && entry.data_type === nextStand.data_type,
    );
  const currentSummary = currentEntry?.stand_summary
    ? (currentEntry.stand_summary as unknown as Record<string, unknown>)
    : null;

  for (const [field, value] of Object.entries(effectiveAttrs)) {
    if (value === undefined) continue;

    if (EFFECTIVE_SITE_FIELDS.has(field)) {
      (nextStand as unknown as Record<string, unknown>)[field] = value;
      (nextSiteCharacteristics as Record<string, unknown>)[field] = value;
      continue;
    }
    const topLevelField = EFFECTIVE_TOP_LEVEL_FIELDS[field];
    if (topLevelField) {
      (nextStand as unknown as Record<string, unknown>)[topLevelField] = coerceNumeric(value);
    }

    const summaryField = EFFECTIVE_SUMMARY_FIELDS[field];
    if (summaryField && currentSummary) {
      currentSummary[summaryField] = coerceNumeric(value);
    }
  }
}

/**
 * Apply the resolved measurement strata onto `nextStand` in place, honouring
 * the selected measured source as one coherent inventory. Missing values stay
 * absent rather than silently retaining the public baseline.
 */
function applyMeasurementStrata(
  nextStand: DetailedForestStand,
  resolved: ResolvedStandStatePayload,
  preferResolvedMeasurementStrata: boolean,
): void {
  if (!preferResolvedMeasurementStrata) {
    return;
  }

  const isMeasuredSource =
    resolved.effective_measurement_source === 'field_scan' ||
    resolved.effective_measurement_source === 'manual';

  // metsakeskus (public) is NOT a measured override: the bundle baseline IS the
  // authoritative public inventory and carries the full species-volume mix +
  // diameter distribution (mean_diameter/stem_count per stratum). The resolved
  // payload's measurement_strata for a public source is only a thin cached echo
  // (e.g. a single volume-only "Cached species" stratum); overlaying it wipes
  // the baseline's diameter distribution and contextual species mix, so the
  // stand-detail story loses its diameter metrics + illustrative media
  // Only field_scan/manual snapshots replace the
  // baseline — mirroring isUsableMeasuredState in sourceResolvedInventory.
  if (!isMeasuredSource) {
    return;
  }

  if (Array.isArray(resolved.measurement_strata)) {
    nextStand.tree_strata = resolved.measurement_strata;
    const currentEntry =
      nextStand.time_series_data?.find((entry) => entry.data_type === '2') ??
      nextStand.time_series_data?.find(
        (entry) => entry.date === nextStand.data_date && entry.data_type === nextStand.data_type,
      );
    if (currentEntry) {
      currentEntry.tree_strata = resolved.measurement_strata;
    }
    const totalVolume = sumStrataMetric(
      resolved.measurement_strata,
      (stratum) => stratum.volume ?? stratum.total_volume ?? null,
    );
    const servedTotal = coerceNumeric(resolved.effective_stand_attributes?.total_volume);
    nextStand.total_volume = totalVolume ?? servedTotal;
    return;
  }
}

const MEASURED_TOP_LEVEL_FIELDS = new Set([
  ...Object.keys(EFFECTIVE_TOP_LEVEL_FIELDS),
  ...Object.values(EFFECTIVE_TOP_LEVEL_FIELDS),
  'basal_area_m2_ha',
  'stems_per_ha',
  'mean_diameter_cm',
  'mean_height_m',
  'dominant_height_m',
]);

const MEASURED_SUMMARY_FIELDS = new Set(Object.values(EFFECTIVE_SUMMARY_FIELDS));

function clearAgeProvenance(metadata: StandDataSourceMetadata): void {
  metadata.age_source = null;
  metadata.borrowed_fields = (metadata.borrowed_fields ?? []).filter((field) => field !== 'age');
  if (metadata.borrowed_field_details) {
    delete metadata.borrowed_field_details.age;
  }
  const provenance = metadata.provenance;
  if (provenance && typeof provenance === 'object') {
    const fields = (provenance as Record<string, unknown>).fields;
    if (fields && typeof fields === 'object') {
      delete (fields as Record<string, unknown>).age;
    }
  }
  const stratumProvenance = metadata.stratum_provenance;
  if (!stratumProvenance || typeof stratumProvenance !== 'object') return;
  for (const stratum of Object.values(stratumProvenance)) {
    if (!stratum || typeof stratum !== 'object') continue;
    const fields = (stratum as Record<string, unknown>).fields;
    if (fields && typeof fields === 'object') {
      delete (fields as Record<string, unknown>).age;
    }
  }
}

function cloneRecordWithFields(value: unknown): unknown {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return value;
  const record = value as Record<string, unknown>;
  const fields = record.fields;
  return {
    ...record,
    ...(fields && typeof fields === 'object' && !Array.isArray(fields)
      ? { fields: { ...(fields as Record<string, unknown>) } }
      : {}),
  };
}

/**
 * Clone every metadata container the resolved-source projection may edit.
 * The public baseline remains an immutable comparison source when users switch
 * repeatedly between Metsakeskus and measured evidence.
 */
function cloneStandDataSourceMetadata(
  metadata: StandDataSourceMetadata | null | undefined,
): StandDataSourceMetadata | null | undefined {
  if (!metadata) return metadata;
  const rawStratumProvenance = metadata.stratum_provenance;
  const stratumProvenance =
    rawStratumProvenance &&
    typeof rawStratumProvenance === 'object' &&
    !Array.isArray(rawStratumProvenance)
      ? Object.fromEntries(
          Object.entries(rawStratumProvenance).map(([key, value]) => [
            key,
            cloneRecordWithFields(value),
          ]),
        )
      : rawStratumProvenance;
  return {
    ...metadata,
    borrowed_fields: metadata.borrowed_fields
      ? [...metadata.borrowed_fields]
      : metadata.borrowed_fields,
    borrowed_field_details: metadata.borrowed_field_details
      ? { ...metadata.borrowed_field_details }
      : metadata.borrowed_field_details,
    simulation_inputs: metadata.simulation_inputs
      ? { ...metadata.simulation_inputs }
      : metadata.simulation_inputs,
    provenance: cloneRecordWithFields(metadata.provenance),
    stratum_provenance: stratumProvenance,
  };
}

function clearMeasuredInventory(nextStand: DetailedForestStand): void {
  const rawStand = nextStand as unknown as Record<string, unknown>;
  for (const field of MEASURED_TOP_LEVEL_FIELDS) {
    delete rawStand[field];
  }
  nextStand.tree_strata = [];
  nextStand.time_series_data = (nextStand.time_series_data ?? []).map((entry) => {
    const standSummary = entry.stand_summary
      ? ({ ...entry.stand_summary } as unknown as Record<string, unknown>)
      : null;
    if (standSummary) {
      for (const field of MEASURED_SUMMARY_FIELDS) {
        standSummary[field] = null;
      }
    }
    return {
      ...entry,
      tree_strata: [],
      stand_summary: standSummary as TreeStandSummary | null,
    };
  });
  const simulationInputs = nextStand.data_source_metadata?.simulation_inputs;
  if (simulationInputs) {
    simulationInputs.dominant_height = null;
  }
  const metadata = nextStand.data_source_metadata;
  if (metadata) clearAgeProvenance(metadata);
}

export function buildUnavailableMeasuredStand(
  baselineStand: DetailedForestStand,
  source: 'field_scan' | 'manual',
): DetailedForestStand {
  const nextStand = normalizeDetailedForestStand({
    ...baselineStand,
    data_source_metadata: {
      ...cloneStandDataSourceMetadata(baselineStand.data_source_metadata),
      effective_measurement_source: source,
      measurement_data_status: 'unavailable',
      measurement_inventory_state: 'unavailable',
    },
  });
  clearMeasuredInventory(nextStand);
  return nextStand;
}

function buildDataSourceMetadata(
  nextStand: DetailedForestStand,
  resolved: ResolvedStandStatePayload,
): DetailedForestStand['data_source_metadata'] {
  const existing = nextStand.data_source_metadata ?? {};
  const isMeasuredSource =
    resolved.effective_measurement_source === 'field_scan' ||
    resolved.effective_measurement_source === 'manual';
  const resolvedCampaignId =
    resolved.campaign_id ??
    (isMeasuredSource ? null : (existing.measurement_campaign_id ?? existing.campaign_id ?? null));
  return {
    ...existing,
    effective_measurement_source: resolved.effective_measurement_source,
    measurement_data_status: resolved.measurement_data_status ?? null,
    measurement_inventory_state: resolved.measurement_inventory_state ?? null,
    // A campaign-less manual source is a complete identity, not permission to
    // inherit the previously active phone-scan campaign from the baseline.
    measurement_campaign_id: resolvedCampaignId,
    campaign_id: resolvedCampaignId,
  };
}

export function applyResolvedStandPayload(
  baselineStand: DetailedForestStand,
  resolved: ResolvedStandStatePayload,
  options?: {
    preferResolvedMeasurementStrata?: boolean;
  },
): DetailedForestStand {
  const preferResolvedMeasurementStrata = options?.preferResolvedMeasurementStrata ?? true;
  const nextStand: DetailedForestStand = normalizeDetailedForestStand({
    ...baselineStand,
    site_characteristics: normalizeSiteCharacteristics(baselineStand.site_characteristics),
    data_source_metadata: baselineStand.data_source_metadata
      ? cloneStandDataSourceMetadata(baselineStand.data_source_metadata)
      : null,
  });

  const isMeasuredSource =
    resolved.effective_measurement_source === 'field_scan' ||
    resolved.effective_measurement_source === 'manual';
  if (preferResolvedMeasurementStrata && isMeasuredSource) {
    // A measured source owns the complete inventory surface. Start from
    // truthful absence, then overlay only values the selected source served.
    clearMeasuredInventory(nextStand);
  }

  const nextSiteCharacteristics: SiteCharacteristics = {
    ...nextStand.site_characteristics,
  };
  applyEffectiveAttributes(
    nextStand,
    nextSiteCharacteristics,
    resolved.effective_stand_attributes ?? {},
  );
  nextStand.site_characteristics = nextSiteCharacteristics;

  applyMeasurementStrata(nextStand, resolved, preferResolvedMeasurementStrata);

  nextStand.resolved_state = buildResolvedMetadata(resolved);
  nextStand.data_source_metadata = buildDataSourceMetadata(nextStand, resolved);

  return normalizeDetailedForestStand(nextStand);
}

export function getCanonicalDominantHeight(stand: DetailedForestStand): number | null {
  const direct = stand.data_source_metadata?.simulation_inputs?.dominant_height;
  if (typeof direct === 'number') {
    return direct;
  }

  const exactMatch = (stand.time_series_data ?? []).find(
    (entry) =>
      entry.date === stand.data_date &&
      entry.data_type === stand.data_type &&
      entry.stand_summary?.dominant_height != null,
  );
  if (exactMatch?.stand_summary?.dominant_height != null) {
    return exactMatch.stand_summary.dominant_height;
  }

  const firstAvailable = (stand.time_series_data ?? []).find(
    (entry) => entry.stand_summary?.dominant_height != null,
  );
  return firstAvailable?.stand_summary?.dominant_height ?? null;
}

export type RestrictionStateSummary =
  | { kind: 'none' }
  | { kind: 'silviculture_only'; count: number }
  | { kind: 'cutting_only'; restriction: string }
  | { kind: 'combined'; restriction: string; silvicultureCount: number };

export function getRestrictionStateSummary(
  site?: SiteCharacteristics | null,
): RestrictionStateSummary {
  const normalized = normalizeSiteCharacteristics(site);
  const silvicultureCount = normalized.silviculture_restrictions?.length ?? 0;

  if (normalized.cutting_restriction) {
    return silvicultureCount > 0
      ? { kind: 'combined', restriction: normalized.cutting_restriction, silvicultureCount }
      : { kind: 'cutting_only', restriction: normalized.cutting_restriction };
  }

  return silvicultureCount > 0
    ? { kind: 'silviculture_only', count: silvicultureCount }
    : { kind: 'none' };
}

export interface TimeSeriesCountSummary {
  measurement: number;
  calculation: number;
  forecast: number;
}

export function getTimeSeriesCounts(stand: DetailedForestStand): TimeSeriesCountSummary {
  const counts = (stand.time_series_data ?? []).reduce<Record<TimeSeriesSemanticType, number>>(
    (acc, entry) => {
      const semanticType = entry.semantic_type ?? classifyTimeSeriesDataType(entry.data_type);
      acc[semanticType] += 1;
      return acc;
    },
    { measurement: 0, calculation: 0, forecast: 0, unknown: 0 },
  );
  return {
    measurement: counts.measurement,
    calculation: counts.calculation,
    forecast: counts.forecast,
  };
}

export function describeRestrictionState(site?: SiteCharacteristics | null): string {
  const normalized = normalizeSiteCharacteristics(site);
  const silvicultureRestrictions = normalized.silviculture_restrictions?.length ?? 0;

  if (normalized.cutting_restriction) {
    return silvicultureRestrictions > 0
      ? `${normalized.cutting_restriction} + ${silvicultureRestrictions} silviculture restrictions`
      : normalized.cutting_restriction;
  }

  return silvicultureRestrictions > 0
    ? `${silvicultureRestrictions} silviculture restrictions`
    : 'none';
}

export function summarizeTimeSeriesSemantics(stand: DetailedForestStand): string {
  const counts = (stand.time_series_data ?? []).reduce<Record<TimeSeriesSemanticType, number>>(
    (acc, entry) => {
      const semanticType = entry.semantic_type ?? classifyTimeSeriesDataType(entry.data_type);
      acc[semanticType] += 1;
      return acc;
    },
    { measurement: 0, calculation: 0, forecast: 0, unknown: 0 },
  );

  return [
    `${counts.measurement} measurement`,
    `${counts.calculation} calculation`,
    `${counts.forecast} forecast`,
  ].join(', ');
}
